'use client'

import React from 'react'

import { Box } from '@chakra-ui/react'

import { Alert } from '../Alert'
import {
  cardFooterStyles,
  cardStepListStyles,
  cardStyles,
} from './ReasoningTrail.styles'
import { ReasoningTrailHeader } from './ReasoningTrailHeader'
import { ReasoningTrailRow } from './ReasoningTrailRow'
import { REASONING_TRAIL_ARIA } from './constants'
import { FlagGlyph } from './icons'
import type { ReasoningTrailCardProps } from './types'

/**
 * AI Operator Reasoning Trail card. Presentational only — it renders the steps
 * and outcome it is given, in array order, and owns no data-fetching or
 * feature-gating (the host app supplies normalized props and wraps this in
 * `DarkProvider`). Build on `Card` primitives for light/dark styling.
 */
export const ReasoningTrailCard = ({
  steps,
  outcome,
  colorScheme = 'dark',
  reviewedStepsLabel,
  footer,
  onViewAccessLogs,
  onToggle,
}: ReasoningTrailCardProps) => {
  return (
    <Box
      {...cardStyles(colorScheme)}
      aria-label={REASONING_TRAIL_ARIA.REGION}
      role='region'
    >
      <ReasoningTrailHeader
        colorScheme={colorScheme}
        reviewedStepsLabel={reviewedStepsLabel}
      />

      {steps.length > 0 && (
        <Box {...cardStepListStyles}>
          {steps.map((step) => (
            <ReasoningTrailRow
              colorScheme={colorScheme}
              emphasized={outcome.isResolved && step.isDecisive}
              key={step.id}
              onToggle={onToggle}
              onViewAccessLogs={onViewAccessLogs}
              step={step}
            />
          ))}
        </Box>
      )}

      {/* Resolved → success (green); escalation → warning (orange) with the
          app's flag glyph, matching the "Flagged" incident affordance. */}
      <Alert
        colorScheme={colorScheme}
        description={outcome.description}
        icon={outcome.isResolved ? undefined : <FlagGlyph />}
        title={outcome.title}
        variant={outcome.isResolved ? 'success' : 'warning'}
      />

      {footer && <Box {...cardFooterStyles(colorScheme)}>{footer}</Box>}
    </Box>
  )
}
